import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SEOMeta from '../components/SEOMeta';

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const Header = styled.header`
  background: white;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0;
`;

const Content = styled.div`
  padding: 24px 16px;
  background: white;
  min-height: calc(100dvh - 60px);
`;

const Section = styled.section`
  margin-bottom: 28px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Paragraph = styled.p`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  margin: 0 0 12px;
`;

const List = styled.ul`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  margin: 0 0 12px;
  padding-left: 20px;
`;

const ListItem = styled.li`
  margin-bottom: 6px;
`;

const OrderedList = styled.ol`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  margin: 0 0 12px;
  padding-left: 20px;
`;

const UpdateDate = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 24px;
`;

const ContactBox = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  margin-top: 24px;
`;

const ContactTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1f2e;
  margin-bottom: 8px;
`;

const ContactEmail = styled.a`
  font-size: 14px;
  color: #2DBCAF;
  text-decoration: none;
`;

const HighlightBox = styled.div<{ type?: 'success' | 'warning' }>`
  background: ${p => p.type === 'warning' ? '#fef3c7' : '#dcfce7'};
  border-left: 4px solid ${p => p.type === 'warning' ? '#f59e0b' : '#22c55e'};
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
`;

const HighlightTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1a1f2e;
  margin-bottom: 8px;
`;

const RefundPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <SEOMeta 
        title="Kebijakan Refund | KinderGame.id"
        description="Kebijakan refund dan pengembalian dana Kindergame"
      />
      
      <Header>
        <BackButton onClick={() => navigate(-1)}>←</BackButton>
        <HeaderTitle>Kebijakan Pengembalian Dana</HeaderTitle>
      </Header>

      <Content>
        <UpdateDate>Terakhir diperbarui: Maret 2026</UpdateDate>

        <Section>
          <SectionTitle>📋 Ketentuan Umum</SectionTitle>
          <Paragraph>
            Kindergame berkomitmen untuk kepuasan pengguna. Jika Anda tidak puas 
            dengan layanan kami, berikut ketentuan pengembalian dana:
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>✅ Syarat Pengajuan Refund</SectionTitle>
          <OrderedList>
            <ListItem><strong>Periode pengajuan:</strong> Maksimal 7 hari sejak tanggal pembayaran</ListItem>
            <ListItem><strong>Penggunaan:</strong> Akun belum digunakan untuk bermain lebih dari 2 game</ListItem>
            <ListItem><strong>Dokumen:</strong> Bukti pembayaran yang valid</ListItem>
          </OrderedList>
        </Section>

        <Section>
          <HighlightBox type="success">
            <HighlightTitle>✓ Kondisi yang Memenuhi Syarat Refund</HighlightTitle>
            <List style={{ margin: 0 }}>
              <ListItem>Kesalahan teknis dari pihak Kindergame yang tidak dapat diperbaiki</ListItem>
              <ListItem>Pembayaran ganda (double charge)</ListItem>
              <ListItem>Fitur tidak sesuai dengan yang dijanjikan</ListItem>
            </List>
          </HighlightBox>

          <HighlightBox type="warning">
            <HighlightTitle>✗ Kondisi yang TIDAK Memenuhi Syarat Refund</HighlightTitle>
            <List style={{ margin: 0 }}>
              <ListItem>Berubah pikiran setelah berlangganan</ListItem>
              <ListItem>Tidak sempat menggunakan layanan</ListItem>
              <ListItem>Lupa membatalkan perpanjangan otomatis (jika ada)</ListItem>
              <ListItem>Akun diblokir karena pelanggaran ketentuan</ListItem>
            </List>
          </HighlightBox>
        </Section>

        <Section>
          <SectionTitle>📝 Cara Mengajukan Refund</SectionTitle>
          <OrderedList>
            <ListItem>
              Kirim email ke <strong>support@kindergame.id</strong> dengan subjek: 
              "Pengajuan Refund - [Nama Anda]"
            </ListItem>
            <ListItem>
              Sertakan: Nama lengkap, email terdaftar, tanggal pembayaran, bukti 
              pembayaran, alasan pengajuan
            </ListItem>
            <ListItem>
              Tim kami akan merespons dalam 1-3 hari kerja
            </ListItem>
          </OrderedList>
        </Section>

        <Section>
          <SectionTitle>⏱️ Proses Pengembalian</SectionTitle>
          <List>
            <ListItem><strong>Waktu proses:</strong> 7-14 hari kerja setelah pengajuan disetujui</ListItem>
            <ListItem><strong>Metode:</strong> Dana dikembalikan ke metode pembayaran asal</ListItem>
            <ListItem><strong>Notifikasi:</strong> Anda akan menerima email saat proses selesai</ListItem>
          </List>
        </Section>

        <ContactBox>
          <ContactTitle>📧 Pertanyaan tentang Refund?</ContactTitle>
          <Paragraph style={{ margin: 0 }}>
            Hubungi kami di:
          </Paragraph>
          <ContactEmail href="mailto:support@kindergame.id">
            support@kindergame.id
          </ContactEmail>
        </ContactBox>
      </Content>
    </PageWrapper>
  );
};

export default RefundPolicyPage;
